import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Search,
  FileText,
  Target,
  BarChart3,
  Settings,
  TrendingUp,
  LogOut,
  ChevronLeft,
  Bug,
  GitBranch,
  Inbox,
  Zap,
  ChevronDown,
  ChevronUp,
  ListChecks
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useOnboarding } from '../contexts/OnboardingContext';
import CreditBadge from './CreditBadge';
import './Sidebar.css';

const APP_NAME = 'Outreach Engine';

// Grouped navigation structure
const navigationGroups = [
  {
    title: 'Core',
    items: [
      { name: 'Dashboard', href: '/', icon: LayoutDashboard },
      { name: 'Auto Pilot', href: '/autopilot', icon: Zap, special: 'autopilot' },
      { name: 'Search', href: '/search', icon: Search },
      { name: 'Prospects', href: '/buyers', icon: Users }
    ]
  },
  {
    title: 'Outreach',
    items: [
      { name: 'Templates', href: '/templates', icon: FileText },
      { name: 'Campaigns', href: '/campaigns', icon: Target },
      { name: 'Inbox', href: '/inbox', icon: Inbox },
      // { name: 'Workflows', href: '/automation', icon: GitBranch }
    ]
  },
  {
    title: 'Insights',
    items: [
      { name: 'Analytics', href: '/analytics', icon: BarChart3 }
    ]
  },
  {
    title: 'Account',
    items: [
      { name: 'Settings', href: '/settings', icon: Settings }
    ]
  }
];

function Sidebar({ collapsed, onToggleCollapse, onReportBug, mobileOpen, onCloseMobile }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { data: onboarding } = useOnboarding();
  const showSetupPill = !!onboarding && !onboarding.complete && !onboarding.dismissed;
  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div className="sidebar-backdrop" onClick={onCloseMobile}></div>
      )}

      <div className={`sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
        <div className="logo">
          <TrendingUp className="logo-icon" />
          <span className="logo-text">{APP_NAME}</span>
        </div>
        <button
          className="sidebar-collapse-toggle"
          onClick={() => {
            if (onToggleCollapse) onToggleCollapse();
            if (mobileOpen && onCloseMobile) onCloseMobile();
          }}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          type="button"
        >
          <ChevronLeft className={`collapse-icon ${collapsed ? 'collapsed' : ''}`} />
        </button>
      </div>

      <nav className="sidebar-nav">
        <ul className="nav-list">
          {navigationGroups.map((group, groupIndex) => (
            <React.Fragment key={group.title}>
              {/* Group Divider */}
              {!collapsed && groupIndex > 0 && (
                <li className="nav-divider">
                  <div className="divider-line"></div>
                </li>
              )}

              {/* Group Title */}
              {!collapsed && (
                <li className="nav-group-title">
                  <span className="group-title-text">{group.title}</span>
                </li>
              )}

              {/* Group Items */}
              {group.items.map((item) => {
                const Icon = item.icon;
                const specialClass = item.special || '';
                return (
                  <li key={item.name} className="nav-item">
                    <Link
                      to={item.href}
                      className={`nav-link ${isActive(item.href) ? 'active' : ''} ${specialClass}`}
                      onClick={() => {
                        if (mobileOpen && onCloseMobile) onCloseMobile();
                      }}
                    >
                      <Icon className="nav-icon" />
                      <span className="nav-text">{item.name}</span>
                    </Link>
                  </li>
                );
              })}
            </React.Fragment>
          ))}
        </ul>
      </nav>

      <div className="sidebar-footer">
        <CreditBadge collapsed={collapsed} />
        {showSetupPill && (
          <Link
            to="/getting-started"
            className="onboarding-pill"
            title="Finish setting up"
            aria-label={`Setup ${onboarding.required_done} of ${onboarding.required_total} done`}
            onClick={() => {
              if (mobileOpen && onCloseMobile) onCloseMobile();
            }}
          >
            <ListChecks size={16} aria-hidden="true" />
            <span className="onboarding-pill-text">Setup {onboarding.required_done}/{onboarding.required_total}</span>
          </Link>
        )}
        <div className="user-info">
          <div className="user-avatar">
            <Users className="avatar-icon" />
          </div>
          <div className="user-details">
            <div className="user-name">{user?.name || user?.email || 'User'}</div>
            <div className="user-role">{user?.email}</div>
          </div>
        </div>
        <button className="logout-button" onClick={handleLogout} title="Logout" type="button">
          <LogOut className="logout-icon" />
          <span>Logout</span>
        </button>
      </div>
    </div>
    </>
  );
}

export default Sidebar;
